// My Profile page — view own info + trigger password change email.
// Available to every authenticated user.
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Lock, Shield, Building2, Users as UsersIcon, BadgeCheck, AlertCircle } from "lucide-react";
import { SignoffCard } from "@/pages/profile/SignoffCard";

export default function MyProfile() {
  const { user, changePassword, rolesCatalog } = useAuth();

  if (!user) {
    return <div className="p-6 text-muted-foreground">Loading profile…</div>;
  }

  const roleInfo = rolesCatalog?.roles?.find(r => r.name === user.role);
  const permsGlossary = rolesCatalog?.permissions || {};

  return (
    <div className="p-6 max-w-3xl space-y-4" data-testid="profile-page">
      <h1 className="text-2xl font-bold text-slate-900">My Account</h1>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <BadgeCheck className="w-4 h-4 text-emerald-600" /> Profile
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <Row label="Name"  value={user.name} />
          <Row label="Email" value={user.email} mono />
          <Row label="User ID" value={user.user_id} mono />
          {user.is_dev_bypass && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2">
              <AlertCircle className="w-3 h-3 inline mr-1" />
              You're signed in via the developer bypass token. Password changes don't apply here.
            </div>
          )}
        </CardContent>
      </Card>

      <SignoffCard />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Shield className="w-4 h-4 text-violet-600" /> Role & permissions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-muted-foreground">Role:</span>
            <Badge variant="outline" className="bg-violet-100 text-violet-800 border-violet-300">{user.role || "— no role —"}</Badge>
            {user.department && <span className="text-muted-foreground">·</span>}
            {user.department && (
              <span className="text-slate-700 inline-flex items-center gap-1"><Building2 className="w-3 h-3" />{user.department}</span>
            )}
            {user.team && (
              <>
                <span className="text-muted-foreground">·</span>
                <span className="text-slate-700 inline-flex items-center gap-1"><UsersIcon className="w-3 h-3" />{user.team}</span>
              </>
            )}
          </div>
          {roleInfo?.description && (
            <p className="text-xs text-muted-foreground italic">{roleInfo.description}</p>
          )}
          <div>
            <div className="text-xs font-semibold text-slate-700 mb-1">Granted permissions ({user.permissions?.length || 0})</div>
            <div className="flex flex-wrap gap-1">
              {(user.permissions || []).map(p => (
                <Badge key={p} variant="outline" className="text-[10px] font-mono bg-emerald-50 border-emerald-200 text-emerald-800" title={permsGlossary[p] || p}>
                  {p}
                </Badge>
              ))}
              {(user.permissions || []).length === 0 && (
                <span className="text-xs text-muted-foreground italic">No permissions yet — ask an admin to assign you a role.</span>
              )}
            </div>
          </div>
          {user.disbursement_limit !== null && user.disbursement_limit !== undefined && (
            <Row label="Disbursement limit" value={`£${Number(user.disbursement_limit).toLocaleString()}`} />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Lock className="w-4 h-4 text-slate-600" /> Password
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm">
          <p className="text-muted-foreground mb-2">
            Click below — we'll email a secure link to set a new password.
            The link expires in 5 days.
          </p>
          <Button onClick={changePassword} disabled={user.is_dev_bypass} data-testid="change-password-btn">
            <Lock className="w-3.5 h-3.5 mr-1" /> Send password reset email
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-xs text-muted-foreground w-32 flex-shrink-0">{label}</span>
      <span className={`text-slate-900 ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</span>
    </div>
  );
}
